import { isProduction } from "../components/global.ts";
import {
    ref,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject
} from "firebase/storage";
import { auth, storage } from "../firebase.ts";

// TODO:
// OPFS bug: on iphone there seems to be a size limitation and videos are not allowed...
// also weird double items when getting from OPFS

interface PhotoOptions {
    dimension?: number; // Output width/height (default 256px)
    quality?: number; // 0 to 1 (default 0.7)
    format?: "image/jpeg" | "image/webp";
    max?: 100 | 1000; // KB
}

interface ValidationResult {
    isValid: boolean;
    errorMessage?: string;
    thumbnail?: string; // Base64 string of the frame
    poster?: File;
}

export interface OPFSResult {
    file: File; // The actual File object (blob)
    directory: string; // The specific subfolder it was found in (e.g., "photos")
    fullPath: string; // The full path (e.g., "media/photos/image.png") // same as: directory + filen.name
}

interface SaveOptions {
    name: string;
    folderPath: string;
    onProgress: (percentage: number) => void;
}

export class Media {
    // TODO:
    // import = // limit = gmail attachment limit
    // // import => stored in 3B crystal // encrypted here
    // if import => don't compress; just as is
    /// upload to firebase storage later upload to Trove
    // upload = // limit short video
    // upload => stored in cloud: trove or youtube or other // encrypted here
    // later // if upload => process: compressed, universal format // later: can store just the firestore doc id at trove ?
    // video: later when logged out local: increase limit to gmail attachemnt
    // video: but difficult now bc need to manager media depending on logged in or not
    // maybe just keep small bc video too large is better stored in the cloud for easier share and unlimited amount too! just need to pay for that storage

    private readonly AVATAR = 100; // 100 KB
    private readonly PHOTO = 1024; // 1 MB // 5 MB rule
    private readonly VIDEO = 50; // 50 MB // 100 MB rule
    private readonly AUDIO_DURATION = 2 * 60; // 2 hours
    private readonly VIDEO_DURATION = 10; // 10 minutes

    // HELPERS

    // not used bc doesn't work on iphone
    private fileToDataURL(file: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                resolve(reader.result as string); // already a data URL
            };

            reader.onerror = (err) => {
                reject(err);
            };

            reader.readAsDataURL(file);
        });
    }

    // PHOTO

    async createAvatarFromFile(
        file: File,
        options: PhotoOptions = {}
    ): Promise<File> {
        const {
            dimension = 256, // 256 or 512 is standard
            quality = 0.8, // 0.8 is the "sweet spot"
            format = "image/webp", // WebP is best for avatars
            max = this.AVATAR // 200KB limit (Plenty for a 512px avatar)
        } = options;

        const MAX_FILE_SIZE = max * 1024;

        // 1. Convert File to Image to get source dimensions
        const image = await this.loadImageFromFile(file);

        // 2. Calculate Math for Center Crop (Calculated once based on source)
        const sourceShortestSide = Math.min(image.width, image.height);
        const sourceX = (image.width - sourceShortestSide) / 2;
        const sourceY = (image.height - sourceShortestSide) / 2;

        // We start with requested settings
        let currentDimension = dimension;
        let currentQuality = quality;
        let blob: Blob | null = null;
        let attempts = 0;
        const MAX_ATTEMPTS = 20; // Prevent infinite loops

        // 3. Compression/Resizing Loop
        while (attempts < MAX_ATTEMPTS) {
            // Create a canvas with the CURRENT dimension
            const canvas = document.createElement("canvas");
            canvas.width = currentDimension;
            canvas.height = currentDimension;
            const ctx = canvas.getContext("2d");

            if (!ctx) throw new Error("Canvas context unavailable");

            // Draw image: Source coords stay same, Dest coords use currentDimension
            ctx.drawImage(
                image,
                sourceX,
                sourceY,
                sourceShortestSide,
                sourceShortestSide, // Source (Crop)
                0,
                0,
                currentDimension,
                currentDimension // Dest (Resize)
            );

            // Convert to Blob
            blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, format, currentQuality)
            );

            if (!blob) throw new Error("Canvas failed to output blob");

            // 4. Check Size
            if (blob.size <= MAX_FILE_SIZE) {
                // Success! We are under 1MB
                break;
            }

            // 5. Logic to Reduce Size
            attempts++;

            // If it's a format that supports quality (JPEG/WebP) and quality is decent
            const supportsQuality =
                format === "image/jpeg" || format === "image/webp";

            if (supportsQuality && currentQuality > 0.5) {
                // Strategy A: Reduce Quality (Fastest, keeps resolution)
                currentQuality -= 0.1;
            } else {
                // Strategy B: Reduce Dimensions (Required for PNG, or if JPEG quality is already low)
                // Reduce dimension by 10%
                currentDimension = Math.floor(currentDimension * 0.9);
            }

            // Safety break: If image gets too tiny, stop compressing
            if (currentDimension < 32) break;
        }

        if (!blob) throw new Error("Failed to process image");

        // 6. Export to File
        return new File([blob], file.name, {
            type: format,
            lastModified: Date.now()
        });
    }

    async createPhotoFromFile(
        file: File,
        options: PhotoOptions = {}
    ): Promise<File> {
        const {
            quality = 0.8,
            format = "image/webp",
            max = this.PHOTO
        } = options;

        const MAX_FILE_SIZE = max * 1024;

        // 1. Convert File to Image to get source dimensions
        const image = await this.loadImageFromFile(file);

        // 2. Initialize dimensions (Full original size, no crop)
        let currentWidth = image.width;
        let currentHeight = image.height;

        let currentQuality = quality;
        let blob: Blob | null = null;
        let attempts = 0;
        const MAX_ATTEMPTS = 20;

        // 3. Compression/Resizing Loop
        while (attempts < MAX_ATTEMPTS) {
            const canvas = document.createElement("canvas");
            canvas.width = currentWidth;
            canvas.height = currentHeight;
            const ctx = canvas.getContext("2d");

            if (!ctx) throw new Error("Canvas context unavailable");

            // Draw the WHOLE image, scaled to the current width/height
            ctx.drawImage(image, 0, 0, currentWidth, currentHeight);

            // Convert to Blob
            blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, format, currentQuality)
            );

            if (!blob) throw new Error("Canvas failed to output blob");

            // 4. Check Size
            if (blob.size <= MAX_FILE_SIZE) {
                break; // Success
            }

            // 5. Logic to Reduce Size
            attempts++;

            const supportsQuality =
                format === "image/jpeg" || format === "image/webp";

            if (supportsQuality && currentQuality > 0.5) {
                // Strategy A: Reduce Quality
                currentQuality -= 0.1;
            } else {
                // Strategy B: Reduce Dimensions (maintain aspect ratio)
                currentWidth = Math.floor(currentWidth * 0.9);
                currentHeight = Math.floor(currentHeight * 0.9);
            }

            // Safety break for very small images
            if (currentWidth < 32 || currentHeight < 32) break;
        }

        if (!blob) throw new Error("Failed to process image");

        // 6. Export to File
        return new File([blob], file.name, {
            type: format,
            lastModified: Date.now()
        });
    }

    /**
     * Helper to load a File object into an HTMLImageElement
     */
    private loadImageFromFile(file: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                // Clean up memory
                URL.revokeObjectURL(objectUrl);
                resolve(img);
            };

            img.onerror = (err) => {
                URL.revokeObjectURL(objectUrl);
                reject(err);
            };

            img.src = objectUrl;
        });
    }

    // VIDEO

    /**
     * Validates a video file and generates a thumbnail.
     */
    async validateVideoFile(file: File): Promise<ValidationResult> {
        // Constants
        const MAX_SIZE_MB = isProduction ? this.VIDEO : 15;
        const MAX_DURATION_MINUTES = isProduction ? this.VIDEO_DURATION : 5;

        // Conversions
        const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
        const MAX_DURATION_SECONDS = MAX_DURATION_MINUTES * 60;

        // 1. Check File Size (Synchronous)
        if (file.size > MAX_SIZE_BYTES) {
            return {
                isValid: false,
                errorMessage: `File size exceeds ${MAX_SIZE_MB}MB.`
            };
        }

        // 2. Check Video Duration and Generate Thumbnail
        return new Promise((resolve) => {
            const video = document.createElement("video");

            // Essential attributes for capturing frames
            video.preload = "metadata";
            video.muted = true;
            video.playsInline = true;

            const videoUrl = URL.createObjectURL(file);
            video.src = videoUrl;

            video.onloadedmetadata = () => {
                // Check duration
                if (video.duration > MAX_DURATION_SECONDS) {
                    URL.revokeObjectURL(videoUrl);
                    resolve({
                        isValid: false,
                        errorMessage: `Video duration exceeds ${MAX_DURATION_MINUTES} minutes.`
                    });
                    return;
                }

                // To capture a thumbnail, we need to seek to a frame (e.g., 1 second)
                // We use Math.min to handle videos shorter than 1 second
                video.currentTime = video.duration / 2; // Math.min(1, video.duration / 2);
            };

            // This triggers after video.currentTime is updated
            video.onseeked = () => {
                try {
                    const canvas = document.createElement("canvas");
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    const ctx = canvas.getContext("2d");
                    if (!ctx)
                        throw new Error("Could not create canvas context");

                    // Draw the current video frame to the canvas
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Convert canvas to base64 image
                    const thumbnail = canvas.toDataURL("image/jpeg", 0.7);
                    const baseName = file.name.replace(/\.[^/.]+$/, ""); // remove extension

                    URL.revokeObjectURL(videoUrl);
                    resolve({
                        isValid: true,
                        thumbnail: thumbnail,
                        poster: this.dataURLtoFile(thumbnail, `${baseName}.jpg`)
                    });
                } catch (err) {
                    URL.revokeObjectURL(videoUrl);
                    resolve({
                        isValid: false,
                        errorMessage: "Failed to generate video thumbnail."
                    });
                }
            };

            video.onerror = () => {
                URL.revokeObjectURL(videoUrl);
                resolve({
                    isValid: false,
                    errorMessage:
                        "Failed to load video. The file might be corrupted or unsupported."
                });
            };
        });
    }

    /**
     * Converts a Base64/Data URL string to a File object.
     *
     * @param dataUrl - The base64 string (e.g., data:image/jpeg;base64,...)
     * @param filename - The desired name for the file (e.g., "thumbnail.jpg")
     * @returns A File object
     */
    private dataURLtoFile(dataUrl: string, filename: string): File {
        // 1. Split the header from the data
        const [header, base64Data] = dataUrl.split(",");

        // 2. Extract the mime type from the header (e.g., "image/jpeg")
        const mimeMatch = header.match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";

        // 3. Decode the base64 string into a binary string
        const binaryString = atob(base64Data);
        let n = binaryString.length;

        // 4. Create a typed array to hold the binary data
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = binaryString.charCodeAt(n);
        }

        // 5. Return the File object
        return new File([u8arr], filename, { type: mime });
    }

    /**
     * Validates a video file based on size and duration.
     * @param file - The HTML File object obtained from an input.
     * @returns Promise<ValidationResult>
     */
    async validateVideoFile2(file: File): Promise<ValidationResult> {
        // Constants
        const MAX_SIZE_MB = isProduction ? this.VIDEO : 15; // storage rule is 15
        const MAX_DURATION_MINUTES = isProduction ? this.VIDEO_DURATION : 15;

        // Conversions
        const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
        const MAX_DURATION_SECONDS = MAX_DURATION_MINUTES * 60;

        // 1. Check File Size (Synchronous)
        if (file.size > MAX_SIZE_BYTES) {
            return {
                isValid: false,
                errorMessage: `File size exceeds ${MAX_SIZE_MB}MB.`
            };
        }

        // 2. Check Video Duration (Asynchronous)
        return new Promise((resolve) => {
            // Create a temporary video element
            const video = document.createElement("video");
            video.preload = "metadata";

            // Create a temporary URL for the file so the video element can read it
            video.src = URL.createObjectURL(file);

            // Event listener: Metadata loaded (we can read duration now)
            video.onloadedmetadata = () => {
                // Release memory used by the object URL
                window.URL.revokeObjectURL(video.src);

                if (video.duration > MAX_DURATION_SECONDS) {
                    resolve({
                        isValid: false,
                        errorMessage: `Video duration exceeds ${MAX_DURATION_MINUTES} minutes.`
                    });
                } else {
                    resolve({ isValid: true });
                }
            };

            // Event listener: Error loading video (e.g., corrupt file or wrong format)
            video.onerror = () => {
                window.URL.revokeObjectURL(video.src);
                resolve({
                    isValid: false,
                    errorMessage:
                        "Failed to load video metadata. The file might be corrupted or format unsupported."
                });
            };
        });
    }

    // AUDIO

    async isAudioValid(file: File): Promise<boolean> {
        // 30 minutes in seconds
        const MAX_DURATION_SECONDS = this.AUDIO_DURATION * 60;

        // 1. Immediate failure for empty files
        if (!file || file.size < 1024) {
            console.error("File is too small or empty");
            return false;
        }

        try {
            // 2. Prepare AudioContext
            // We use OfflineAudioContext to avoid using hardware resources/limits
            // 44100 is standard, length 1, 1 channel (dummy values, we just want to decode)
            const AudioContextClass =
                window.AudioContext || (window as any).webkitAudioContext;
            const audioContext = new AudioContextClass();

            // 3. Convert File to ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();

            // 4. Decode the raw data (This works even if <audio> gives Error 4)
            // This is CPU intensive but accurate.
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            // 5. Cleanup
            if (audioContext.state !== "closed") {
                await audioContext.close();
            }

            const duration = audioBuffer.duration;

            console.log(`Audio validated. Duration: ${duration.toFixed(2)}s`);

            // 6. Validate Duration
            if (duration > MAX_DURATION_SECONDS) {
                console.warn("Audio too long");
                return false;
            }

            return true;
        } catch (error) {
            console.error("Audio validation failed:", error);
            // If decodeAudioData fails, the file is corrupt or truly unsupported
            return false;
        }
    }

    /**
     * Checks if an audio file is less than 30 minutes long.
     *
     * @param file - The File object (from an input element)
     * @returns Promise<boolean> - True if < 30 mins, False if >= 30 mins
     */
    private isAudioValid2(file: File): Promise<boolean> {
        const MAX_DURATION_SECONDS = this.AUDIO_DURATION * 60; // 30 minutes in seconds

        return new Promise((resolve, reject) => {
            // Optional: Quick check for MIME type
            if (!file.type.startsWith("audio/")) {
                reject(new Error("The provided file is not an audio file."));
                return;
            }

            // Create an HTMLAudioElement
            const audio = new Audio();

            // Create a temporary URL for the file
            const objectUrl = URL.createObjectURL(file);

            // Event listener: Triggered when duration metadata is available
            audio.onloadedmetadata = () => {
                const duration = audio.duration; // Duration is in seconds

                // Clean up memory
                URL.revokeObjectURL(objectUrl);

                // Check if duration is valid (sometimes returns Infinity for streams)
                if (duration === Infinity || isNaN(duration)) {
                    reject(new Error("Could not determine audio duration."));
                    return;
                }

                if (duration < MAX_DURATION_SECONDS) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            };

            // Event listener: Triggered if the file is corrupt or unreadable
            audio.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error("Failed to load audio file."));
            };

            // Start loading the file
            audio.src = objectUrl;
        });
    }

    async extractAudioFromVideo(videoFile: File): Promise<File> {
        return new Promise(async (resolve, reject) => {
            // 1. Setup AudioContext
            const AudioContextClass =
                window.AudioContext || (window as any).webkitAudioContext;
            const audioContext = new AudioContextClass();

            if (audioContext.state === "suspended") {
                try {
                    await audioContext.resume();
                } catch (e) {
                    return reject(
                        new Error(
                            "AudioContext suspended. Call this from a click event."
                        )
                    );
                }
            }

            // 2. Setup Invisible Video Element (iOS Requirements)
            const videoElement = document.createElement("video");
            videoElement.setAttribute("playsinline", "true");
            videoElement.setAttribute("webkit-playsinline", "true");
            videoElement.preload = "metadata";

            // Attach to DOM (Invisible) - Required for playback on iOS
            videoElement.style.position = "fixed";
            videoElement.style.top = "0";
            videoElement.style.left = "0";
            videoElement.style.width = "1px";
            videoElement.style.height = "1px";
            videoElement.style.opacity = "0";
            videoElement.style.pointerEvents = "none";
            videoElement.style.zIndex = "-1000";
            document.body.appendChild(videoElement);

            const videoUrl = URL.createObjectURL(videoFile);
            videoElement.src = videoUrl;
            videoElement.load();

            // 3. Prepare State
            let source: MediaElementAudioSourceNode | null = null;
            let destination: MediaStreamAudioDestinationNode | null = null;
            let gainNode: GainNode | null = null;
            let mediaRecorder: MediaRecorder | null = null;
            const chunks: BlobPart[] = [];

            const cleanup = () => {
                if (document.body.contains(videoElement))
                    document.body.removeChild(videoElement);
                URL.revokeObjectURL(videoUrl);
                source?.disconnect();
                gainNode?.disconnect();
                // Don't close AudioContext immediately if you plan to reuse it,
                // but for a one-off function, closing is fine.
                if (audioContext.state !== "closed") audioContext.close();
            };

            videoElement.onloadedmetadata = () => {
                try {
                    // Connect the graph
                    source =
                        audioContext.createMediaElementSource(videoElement);
                    destination = audioContext.createMediaStreamDestination();
                    gainNode = audioContext.createGain();

                    // Mute Output (Silent Gain) to speakers so iOS processes data
                    gainNode.gain.value = 0;

                    source.connect(gainNode);
                    gainNode.connect(audioContext.destination);

                    // Connect Source to Destination for Recording
                    source.connect(destination);

                    // FIX 1: Extract ONLY Audio Tracks
                    // If we don't do this, Safari adds a blank video track which corrupts 'audio/mp4' files
                    const audioTracks = destination.stream.getAudioTracks();
                    const audioOnlyStream = new MediaStream(audioTracks);

                    // Determine MimeType
                    const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
                        ? "audio/mp4"
                        : "audio/webm;codecs=opus";
                    // let mimeType = "audio/webm";
                    // if (MediaRecorder.isTypeSupported("audio/mp4")) {
                    //     mimeType = "audio/mp4"; // iOS Standard
                    // } else if (
                    //     MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                    // ) {
                    //     mimeType = "audio/webm;codecs=opus";
                    // }

                    mediaRecorder = new MediaRecorder(audioOnlyStream, {
                        mimeType
                    });

                    mediaRecorder.ondataavailable = (e) => {
                        if (e.data.size > 0) chunks.push(e.data);
                    };

                    mediaRecorder.onstop = () => {
                        cleanup();

                        // FIX 2: Check for Empty File
                        const blob = new Blob(chunks, { type: mimeType });
                        if (blob.size < 100) {
                            reject(
                                new Error(
                                    "Extraction failed: Output file is empty."
                                )
                            );
                            return;
                        }

                        const ext = mimeType.includes("mp4") ? "m4a" : "webm";
                        // const fileName =
                        //     videoFile.name.replace(/\.[^/.]+$/, "") + `.${ext}`;
                        // const file = new File([blob], fileName, { type: mimeType });
                        const file = new File([blob], `voice.${ext}`, {
                            type: `audio/${ext}`
                        });

                        resolve(file);
                    };

                    mediaRecorder.onerror = (e) => {
                        cleanup();
                        reject(e);
                    };

                    // Start Playback
                    // FIX 3: Moderate Speed.
                    // 4x speed on iOS often causes empty buffers. 2x is reliable.
                    videoElement.playbackRate = 2.0;
                    videoElement.muted = false;

                    // FIX 4: Use Timeslice (500ms)
                    // iOS MediaRecorder fails to write data if we don't request chunks regularly
                    mediaRecorder.start(500);

                    videoElement.play().catch((e) => {
                        cleanup();
                        reject(
                            new Error(
                                "Playback failed. Call from click handler. " +
                                    e.message
                            )
                        );
                    });
                } catch (err: any) {
                    cleanup();
                    reject(new Error("Setup failed: " + err.message));
                }
            };

            videoElement.onended = () => {
                // Small delay to ensure the last chunk is captured
                setTimeout(() => {
                    mediaRecorder?.stop();
                }, 100);
            };

            videoElement.onerror = (e) => {
                cleanup();
                reject(new Error("Video Load Error"));
            };
        });
    }

    /**
     * Extracts audio from a video file using MediaStream and MediaRecorder.
     *
     * iOS Compatibility Features:
     * 1. Attaches video to DOM (invisible) to allow playback.
     * 2. Uses 'playsinline' to prevent fullscreen.
     * 3. Routes audio to a silent GainNode to keep the audio subsystem active on iOS.
     *
     * @param videoFile The source video File
     * @returns A Promise resolving to the audio File (m4a or webm)
     */
    private async extractAudioFromVideo3(videoFile: File): Promise<File> {
        return new Promise(async (resolve, reject) => {
            // 1. Setup AudioContext (Handles standard and WebKit prefix)
            const AudioContextClass =
                window.AudioContext || (window as any).webkitAudioContext;
            const audioContext = new AudioContextClass();

            // iOS Fix: Resume context if suspended (must happen during user interaction)
            if (audioContext.state === "suspended") {
                try {
                    await audioContext.resume();
                } catch (e) {
                    reject(
                        new Error(
                            "AudioContext suspended. Call this function inside a click handler."
                        )
                    );
                    return;
                }
            }

            // 2. Setup Video Element with iOS workarounds
            const videoUrl = URL.createObjectURL(videoFile);
            const videoElement = document.createElement("video");
            videoElement.src = videoUrl;
            videoElement.crossOrigin = "anonymous";

            // iOS CRITICAL: Attributes
            videoElement.setAttribute("playsinline", "true");
            videoElement.setAttribute("webkit-playsinline", "true");

            // iOS CRITICAL: Must be in DOM, but invisible
            videoElement.style.position = "fixed";
            videoElement.style.top = "0";
            videoElement.style.left = "0";
            videoElement.style.width = "1px";
            videoElement.style.height = "1px";
            videoElement.style.opacity = "0";
            videoElement.style.pointerEvents = "none";
            videoElement.style.zIndex = "-1000";
            document.body.appendChild(videoElement);

            // 3. Setup Audio Routing
            const source = audioContext.createMediaElementSource(videoElement);
            const destination = audioContext.createMediaStreamDestination();

            // iOS CRITICAL: "Silent Gain" Trick
            // We must route audio to hardware speakers for iOS to process the stream,
            // but we use a GainNode at 0 volume so the user doesn't hear it.
            const gainNode = audioContext.createGain();
            gainNode.gain.value = 0;

            // Connect: Source -> Recorder
            source.connect(destination);

            // Connect: Source -> Silent Gain -> Hardware (Keep-alive for iOS)
            source.connect(gainNode);
            gainNode.connect(audioContext.destination);

            const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
                ? "audio/mp4"
                : "audio/webm;codecs=opus";
            // 4. Determine MimeType (Prefer mp4 for iOS, webm for Chrome)
            // let mimeType = "";
            // if (MediaRecorder.isTypeSupported("audio/mp4")) {
            //     mimeType = "audio/mp4"; // Safari / iOS
            // } else if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
            //     mimeType = "audio/webm;codecs=opus"; // Chrome / Firefox
            // } else {
            //     mimeType = "audio/webm";
            // }

            const mediaRecorder = new MediaRecorder(destination.stream, {
                mimeType
            });
            const chunks: BlobPart[] = [];

            // 5. Event Handlers
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) chunks.push(event.data);
            };

            const cleanup = () => {
                if (document.body.contains(videoElement)) {
                    document.body.removeChild(videoElement);
                }
                URL.revokeObjectURL(videoUrl);
                source.disconnect();
                gainNode.disconnect();
                audioContext.close();
            };

            mediaRecorder.onstop = () => {
                cleanup();

                // Determine extension based on mimeType
                const ext = mimeType.includes("mp4") ? "m4a" : "webm";
                const blob = new Blob(chunks, { type: mimeType });
                // const fileName =
                //     videoFile.name.replace(/\.[^/.]+$/, "") + `.${ext}`;
                // const audioFile = new File([blob], fileName, { type: mimeType });
                const audioFile = new File([blob], `voice.${ext}`, {
                    type: `audio/${ext}`
                });

                resolve(audioFile);
            };

            mediaRecorder.onerror = (e) => {
                cleanup();
                reject(e);
            };

            videoElement.onended = () => {
                mediaRecorder.stop();
            };

            videoElement.onerror = (e) => {
                cleanup();
                reject(new Error("Video playback failed"));
            };

            // 6. Start Process
            videoElement.onloadedmetadata = () => {
                // Speed up processing (2x is usually safe for iOS)
                videoElement.playbackRate = 2.0;

                // Ensure video is "unmuted" logically so data flows
                videoElement.muted = false;

                mediaRecorder.start();

                videoElement.play().catch((err) => {
                    cleanup();
                    reject(
                        new Error(
                            "Autoplay denied. Ensure function is called from a click event. " +
                                err.message
                        )
                    );
                });
            };
        });
    }

    /**
     * Extracts audio from a video file using MediaStream and MediaRecorder.
     *
     * Note: Since this uses MediaRecorder, the extraction happens in real-time
     * (or faster if playbackRate is increased), meaning a 10-second video
     * takes roughly 10 seconds to process.
     *
     * @param videoFile The source video File object
     * @returns A Promise resolving to the audio Blob (usually audio/webm or audio/mp4)
     */
    private async extractAudioFromVideo2(videoFile: File): Promise<File> {
        return new Promise((resolve, reject) => {
            // 1. Create a URL for the file to play it
            const videoUrl = URL.createObjectURL(videoFile);

            // 2. Create a hidden video element
            const videoElement = document.createElement("video");
            videoElement.src = videoUrl;
            videoElement.crossOrigin = "anonymous"; // Important if handling external URLs

            // 3. Setup Audio Context
            // We use standard AudioContext to route audio without playing it through speakers
            const audioContext = new (
                window.AudioContext || (window as any).webkitAudioContext
            )();

            // 4. Create a MediaStream Destination
            // This allows us to capture the audio processing graph as a MediaStream
            const destination = audioContext.createMediaStreamDestination();

            // 5. Connect Video Element -> MediaStream Destination
            const source = audioContext.createMediaElementSource(videoElement);
            source.connect(destination);

            // NOTE: We do NOT connect to audioContext.destination.
            // This effectively "mutes" the playback for the user while still sending data to the recorder.

            // 6. Initialize MediaRecorder with the stream
            // Check supported mime types for better compatibility
            // const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
            //     ? "audio/webm;codecs=opus"
            //     : "audio/mp4";
            const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
                ? "audio/mp4"
                : "audio/webm;codecs=opus"; // this format looks like video per OPFS

            const mediaRecorder = new MediaRecorder(destination.stream, {
                mimeType
            });
            const chunks: BlobPart[] = [];

            // 7. Event Handlers
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                // Cleanup
                URL.revokeObjectURL(videoUrl);
                audioContext.close();
                videoElement.remove();

                // Create the final Audio File
                const blob = new Blob(chunks, { type: mimeType });
                const extension = mimeType.includes("mp4") ? "m4a" : "webm";
                const audioFile = new File([blob], `voice.${extension}`, {
                    type: `audio/${extension}`
                });

                resolve(audioFile);
            };

            mediaRecorder.onerror = (e) => {
                reject(e);
            };

            // 8. Handle Video Playback logic
            videoElement.onloadedmetadata = () => {
                // Optional: Speed up playback to extract faster.
                // Note: Going too fast (e.g., > 4x) might cause dropping frames/audio artifacts in some browsers.
                videoElement.playbackRate = 2.0;

                mediaRecorder.start();
                videoElement.play().catch(reject);
            };

            videoElement.onended = () => {
                mediaRecorder.stop();
            };

            videoElement.onerror = (e) => {
                reject(new Error("Error loading video file"));
            };
        });
    }

    /// CLOUD STORAGE

    async storeFile(
        name: string,
        file: File,
        progressCallback: (progress: number) => void
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            // 1. Create the upload task
            const user = auth.currentUser;
            if (!user) return reject(new Error("Authenticated users only"));

            const storageRef = ref(
                storage,
                `users/${user.uid}/vault/${Date.now().toString(36)}`
            );
            const uploadTask = uploadBytesResumable(storageRef, file, {
                customMetadata: {
                    name,
                    type: file.type
                }
            });

            // 2. Register the event listener
            uploadTask.on(
                "state_changed",
                // Observer for progress
                (snapshot) => {
                    const progress = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    );
                    //console.log("Upload is " + progress + "% done");

                    // Call the progress callback if it exists
                    if (progressCallback) {
                        progressCallback(progress);
                    }
                },
                // Observer for errors
                (error) => {
                    // Handle unsuccessful uploads and reject the promise
                    //console.error("Upload failed:", error);
                    reject(error);
                },
                // Observer for completion
                async () => {
                    try {
                        // Handle successful uploads on complete
                        //console.log("Upload complete, getting download URL...");
                        const downloadURL = await getDownloadURL(
                            uploadTask.snapshot.ref
                        );
                        // Resolve the promise with the download URL
                        resolve(downloadURL);
                    } catch (error) {
                        // If getting the URL fails, reject the promise
                        //console.error("Failed to get download URL:", error);
                        reject(error);
                    }
                }
            );
        });
    }

    async removeFile(fileUrl: string): Promise<string> {
        const user = auth.currentUser;
        if (!user) throw new Error("Authenticated users only");

        // 2. Create a reference to the file to delete.
        // Firebase Storage SDK can parse the gs:// or https:// URL to get a reference.
        const fileRef = ref(storage, fileUrl);
        // 3. Delete the file from Cloud Storage
        await deleteObject(fileRef);
        //console.log("File deleted from Storage successfully.");
        //console.log("User photoURL removed from Auth profile.");

        // 5. Update the UI
        return "File deleted successfully.";
    }

    //// LOCAL STORAGE: OPFS

    // count

    /**
     * Recursively counts all files inside a specific OPFS folder.
     *
     * @param folderPath - The folder to check (e.g., "media")
     * @returns The total number of files found. Returns 0 if folder doesn't exist.
     */
    async countFilesInFolder(folderPath: string): Promise<number> {
        try {
            const root = await navigator.storage.getDirectory();
            let targetDir = root;

            // 1. Navigate to the specific folder
            if (folderPath) {
                const parts = folderPath.split("/").filter((p) => p.length > 0);
                for (const part of parts) {
                    targetDir = await targetDir.getDirectoryHandle(part);
                }
            }

            // 2. Start counting recursively
            return await this.countFilesRecursively(targetDir);
        } catch (error: any) {
            // If the folder "media" doesn't exist, strictly speaking, there are 0 files.
            if (error.name === "NotFoundError") {
                return 0;
            }
            console.error("Error counting files:", error);
            throw error;
        }
    }

    /**
     * Helper: Recursive walker
     */
    private async countFilesRecursively(
        dirHandle: FileSystemDirectoryHandle
    ): Promise<number> {
        let count = 0;

        // @ts-ignore - Ensure "DOM.Iterable" is in tsconfig lib
        for await (const [name, handle] of dirHandle.entries()) {
            if (handle.kind === "file") {
                count++;
            } else if (handle.kind === "directory") {
                // If it's a folder, go inside and add its count to our total
                const subDirCount = await this.countFilesRecursively(
                    handle as FileSystemDirectoryHandle
                );
                count += subDirCount;
            }
        }

        return count;
    }

    //// save

    /**
     * Saves ANY file to OPFS.
     * Automatically handles large files by chunking to prevent UI freezing
     * and provides progress updates.
     */
    async saveFileToOPFS(file: File, options: SaveOptions) {
        const { name, folderPath = "", onProgress } = options;
        const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunks

        try {
            // 1. Get Root and Resolve Directory
            const root = await navigator.storage.getDirectory();
            const targetDir = folderPath
                ? await this.getDirectoryHandleRecursive(root, folderPath)
                : root;

            // 2. Create File Handle
            const fileHandle = await targetDir.getFileHandle(name, {
                create: true
            });

            // 3. Create Writable Stream
            const writable = await fileHandle.createWritable();

            // 4. Write Content (Chunked logic for memory efficiency & progress)
            const totalSize = file.size;
            let offset = 0;

            // If file is empty, just write it
            if (totalSize === 0) {
                await writable.write(file);
                if (onProgress) onProgress(100);
            } else {
                while (offset < totalSize) {
                    const chunk = file.slice(offset, offset + CHUNK_SIZE);
                    await writable.write(chunk);

                    offset += chunk.size;

                    if (onProgress) {
                        // Cap progress at 99% until the file is actually closed/flushed
                        const percent = Math.min(
                            99,
                            Math.round((offset / totalSize) * 100)
                        );
                        onProgress(percent);
                    }
                }
            }

            // 5. Close/Flush (This is where the actual save to disk finalizes)
            await writable.close();

            if (onProgress) onProgress(100);
        } catch (error) {
            console.error(`Error saving ${name} to OPFS:`, error);
            throw error;
        }
    }

    /// get

    /**
     * Helper: Recursively gets (or creates) a directory handle from a path string.
     * Example: 'media/videos' -> gets root -> gets media -> gets videos
     */
    private async getDirectoryHandleRecursive(
        root: FileSystemDirectoryHandle,
        path: string
    ): Promise<FileSystemDirectoryHandle> {
        const parts = path.split("/").filter((part) => part.length > 0);
        let currentHandle = root;

        for (const part of parts) {
            currentHandle = await currentHandle.getDirectoryHandle(part, {
                create: true
            });
        }

        return currentHandle;
    }

    /**
     * Retrieves a file from OPFS.
     * Returns a standard File object which can be used for URL.createObjectURL, uploads, etc.
     */
    async getFileFromOPFS(filename: string, folderPath: string): Promise<File> {
        try {
            const root = await navigator.storage.getDirectory();

            const targetDir = folderPath
                ? await this.getDirectoryHandleRecursive(root, folderPath)
                : root;

            const fileHandle = await targetDir.getFileHandle(filename);
            const file = await fileHandle.getFile();

            return file;
        } catch (error) {
            console.error(`Error retrieving ${filename} from OPFS:`, error);
            throw error;
        }
    }

    async getFilesFromOPFS(folderPath: string): Promise<File[]> {
        const trove = await this.getAllFilesInFolder(folderPath);
        return trove.map((diamond) => diamond.file);
    }

    /**
     * Retrieves all files recursively from a specific OPFS folder.
     *
     * @param folderPath - The root folder to search (e.g., "media")
     * @returns An array of objects containing the File and its path info.
     */
    async getAllFilesInFolder(folderPath: string): Promise<OPFSResult[]> {
        const fileList: OPFSResult[] = [];

        try {
            const root = await navigator.storage.getDirectory();

            // 1. Get the handle for the requested folder (e.g., "media")
            // If folderPath is empty, we start at the root.
            let startDir = root;
            if (folderPath) {
                try {
                    startDir = await this.getDirectoryHandleRecursive(
                        root,
                        folderPath
                    );
                } catch (e) {
                    console.warn(`Folder "${folderPath}" does not exist.`);
                    return [];
                }
            }

            // 2. Start the recursive search
            await this.traverseDirectory(startDir, folderPath, fileList);

            return fileList;
        } catch (error) {
            console.error("Error scanning OPFS:", error);
            return [];
        }
    }

    /**
     * Helper: Recursively walks through folders to find files
     */
    private async traverseDirectory(
        dirHandle: FileSystemDirectoryHandle,
        currentPath: string,
        fileList: OPFSResult[]
    ) {
        // @ts-ignore - 'entries' exists in modern browsers but TS might need "dom.iterable" lib
        for await (const [name, handle] of dirHandle.entries()) {
            const relativePath = currentPath ? `${currentPath}/${name}` : name;

            if (handle.kind === "file") {
                // It's a file: Get the data and add to list
                const fileHandle = handle as FileSystemFileHandle;
                const file = await fileHandle.getFile();

                fileList.push({
                    file: file,
                    directory: currentPath, // e.g. "media/photos"
                    fullPath: relativePath // e.g. "media/photos/cat.jpg"
                });
            } else if (handle.kind === "directory") {
                // It's a folder: Go deeper (Recursion)
                const subDirHandle = handle as FileSystemDirectoryHandle;
                await this.traverseDirectory(
                    subDirHandle,
                    relativePath,
                    fileList
                );
            }
        }
    }

    // move

    /**
     * Moves a file from one folder to another in OPFS.
     *
     * @param filename - The name of the file (e.g., "vacation.jpg")
     * @param oldFolderPath - The current folder path (e.g., "downloads")
     * @param newFolderPath - The destination folder path (e.g., "media/photos")
     */
    async moveFileInOPFS(
        filename: string,
        oldFolderPath: string,
        newFolderPath: string
    ): Promise<void> {
        // Optimization: If paths are the same, do nothing.
        if (oldFolderPath === newFolderPath) return;

        try {
            const root = await navigator.storage.getDirectory();

            // 1. Get the source directory (must exist, so create: false)
            const sourceDir = await this.getDirectoryHandleFromPath(
                root,
                oldFolderPath,
                false
            );

            // 2. Get the file handle from the source
            const fileHandle = await sourceDir.getFileHandle(filename);

            // 3. Get (or create) the destination directory
            // We use create: true to ensure the target path exists.
            const destDir = await this.getDirectoryHandleFromPath(
                root,
                newFolderPath,
                true
            );

            // 4. Move the file
            // The .move() method takes the destination directory handle.
            // Note: This method is available in Chromium browsers.
            // If you need to support non-Chromium browsers strictly right now,
            // you might need a polyfill (Read -> Write New -> Delete Old).
            // @ts-ignore - Typescript might not recognise .move() depending on your lib version
            await fileHandle.move(destDir);

            console.log(
                `Successfully moved ${filename} from '${oldFolderPath}' to '${newFolderPath}'`
            );
        } catch (error: any) {
            if (error.name === "NotFoundError") {
                console.warn(
                    `Could not move: File or Source folder not found (${oldFolderPath}/${filename})`
                );
                return;
            }
            console.error("Error moving file:", error);
            throw error;
        }
    }

    /**
     * Helper to navigate to a specific directory handle.
     * @param root - The root directory handle
     * @param path - The string path (e.g. "media/photos")
     * @param create - Whether to create folders if they don't exist
     */
    private async getDirectoryHandleFromPath(
        root: FileSystemDirectoryHandle,
        path: string,
        create: boolean
    ): Promise<FileSystemDirectoryHandle> {
        let currentDir = root;
        const parts = path.split("/").filter((p) => p.length > 0);

        for (const part of parts) {
            currentDir = await currentDir.getDirectoryHandle(part, { create });
        }
        return currentDir;
    }

    // delete

    /**
     * Deletes a specific file from OPFS.
     *
     * @param filename - The name of the file (e.g., "vacation.jpg")
     * @param folderPath - The folder path (e.g., "media/photos")
     */
    async deleteFileFromOPFS(
        filename: string,
        folderPath: string
    ): Promise<void> {
        try {
            const root = await navigator.storage.getDirectory();
            let targetDir = root;

            // 1. Navigate to the specific folder
            if (folderPath) {
                const parts = folderPath.split("/").filter((p) => p.length > 0);

                for (const part of parts) {
                    // We use create: false because if the folder doesn't exist,
                    // we can't delete the file anyway.
                    targetDir = await targetDir.getDirectoryHandle(part, {
                        create: false
                    });
                }
            }

            // 2. Remove the file
            // The second argument { recursive: false } is default for files.
            await targetDir.removeEntry(filename);

            console.log(`Successfully deleted: ${folderPath}/${filename}`);
        } catch (error: any) {
            // Handle case where file or folder doesn't exist
            if (error.name === "NotFoundError") {
                console.warn(
                    `Could not delete: File or folder not found (${folderPath}/${filename})`
                );
                return; // Fail silently or throw depending on your needs
            }
            console.error("Error deleting file:", error);
            throw error;
        }
    }

    async deleteFolderFromOPFS(folderPath: string): Promise<void> {
        try {
            const root = await navigator.storage.getDirectory();

            // We need the parent directory to delete the child directory
            const parts = folderPath.split("/").filter((p) => p.length > 0);
            const folderToDelete = parts.pop(); // The last part is the folder name
            const parentPath = parts.join("/"); // The rest is the parent path

            // Navigate to parent
            let parentDir = root;
            if (parentPath) {
                // Reuse a getDirectoryRecursive helper here or manual loop
                const parentParts = parentPath.split("/");
                for (const part of parentParts) {
                    parentDir = await parentDir.getDirectoryHandle(part, {
                        create: false
                    });
                }
            }

            if (folderToDelete) {
                // recursive: true allows deleting a folder that has files inside it
                await parentDir.removeEntry(folderToDelete, {
                    recursive: true
                });
                console.log(`Deleted folder: ${folderPath}`);
            }
        } catch (error) {
            console.error("Error deleting folder:", error);
        }
    }

    // not used
    private async hardResetMediaFolder() {
        const root = await navigator.storage.getDirectory();

        try {
            // 1. Delete 'media' and EVERYTHING inside it
            await root.removeEntry("media", { recursive: true });
        } catch (e) {
            // Ignore error if 'media' didn't exist yet
        }

        // 2. Recreate empty 'media' folder
        const mediaHandle = await root.getDirectoryHandle("media", {
            create: true
        });

        // 3. (Optional) Recreate subfolders immediately
        await mediaHandle.getDirectoryHandle("photos", { create: true });
        await mediaHandle.getDirectoryHandle("videos", { create: true });

        console.log("Media folder reset complete.");
    }
}
